import { z } from "zod";
import { createEndpoint, createEndpointWithPagination } from "../createEndpoint";
import type { HttpClient } from "../httpClient";
import {
    ExpertCreatePayoutRequestRequestSchema,
    ExpertPayoutRequestSchema,
    ExpertQuoteFeeEstimateSchema,
    ExpertWalletLevelProgressSchema,
    ExpertWalletSummarySchema,
    ExpertWalletTransactionSchema,
    type ExpertPayoutRequestsQueryParams,
    type ExpertQuoteFeeEstimateRequest,
    type ExpertWalletActingOrgQueryParams,
    type ExpertWalletTransactionsQueryParams,
} from "../schemas/wallet";

export function createExpertWalletApi(http: HttpClient, baseUrl: string) {
    const expertWalletBaseUrl = `${baseUrl}/expert/wallet`;
    const expertPayoutRequestsBaseUrl = `${baseUrl}/expert/payout-requests`;

    return {
        getWallet: createEndpoint<
            z.infer<typeof ExpertWalletSummarySchema>,
            unknown,
            ExpertWalletActingOrgQueryParams
        >(http, "GET", "", {
            base: expertWalletBaseUrl,
            responseSchema: ExpertWalletSummarySchema,
        }),

        getLevelProgress: createEndpoint<
            z.infer<typeof ExpertWalletLevelProgressSchema>,
            unknown,
            ExpertWalletActingOrgQueryParams
        >(http, "GET", "/level", {
            base: expertWalletBaseUrl,
            responseSchema: ExpertWalletLevelProgressSchema,
        }),

        estimateQuoteFees: createEndpoint<
            z.infer<typeof ExpertQuoteFeeEstimateSchema>,
            ExpertQuoteFeeEstimateRequest
        >(http, "POST", "/estimate-breakdown", {
            base: expertWalletBaseUrl,
            responseSchema: ExpertQuoteFeeEstimateSchema,
        }),

        listTransactions: createEndpointWithPagination<
            z.infer<typeof ExpertWalletTransactionSchema>,
            unknown,
            unknown,
            ExpertWalletTransactionsQueryParams
        >(http, "GET", "/transactions", {
            base: expertWalletBaseUrl,
            itemSchema: ExpertWalletTransactionSchema,
        }),

        getTransaction: createEndpoint<
            z.infer<typeof ExpertWalletTransactionSchema>,
            unknown,
            ExpertWalletActingOrgQueryParams,
            { transaction: number }
        >(http, "GET", "/transactions/:transaction", {
            base: expertWalletBaseUrl,
            responseSchema: ExpertWalletTransactionSchema,
            params: ["transaction"],
        }),

        listPayoutRequests: createEndpointWithPagination<
            z.infer<typeof ExpertPayoutRequestSchema>,
            unknown,
            unknown,
            ExpertPayoutRequestsQueryParams
        >(http, "GET", "", {
            base: expertPayoutRequestsBaseUrl,
            itemSchema: ExpertPayoutRequestSchema,
        }),

        createPayoutRequest: createEndpoint<
            z.infer<typeof ExpertPayoutRequestSchema>,
            z.input<typeof ExpertCreatePayoutRequestRequestSchema>
        >(http, "POST", "", {
            base: expertPayoutRequestsBaseUrl,
            responseSchema: ExpertPayoutRequestSchema,
        }),
    };
}

export type ExpertWalletApi = ReturnType<typeof createExpertWalletApi>;
